import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, Lock, HeartHandshake, MessageSquareHeart, Stethoscope,
  FlaskConical, Users, BookOpen, EyeOff, FileLock2, ShieldCheck, Languages,
} from "lucide-react";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import AppointmentDialog from "@/components/AppointmentDialog";
import AnonymousEnquiry from "@/components/AnonymousEnquiry";
import useSEO from "@/hooks/useSEO";

const HERO = "https://images.unsplash.com/photo-1604881991720-f91add269bed?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHw0fHxjb3VwbGUlMjBob2xkaW5nJTIwaGFuZHMlMjB3YXJtJTIwY2FsbSUyMGNvdW5zZWxsaW5nfGVufDB8fHx8MTc4ODE3OTAzNXww&ixlib=rb-4.1.0&q=85";
const SITE = "https://manasneuropsychiatryhospital.in";

const groups = [
  { label: "પુરુષોની જાતીય તકલીફો", items: ["શિશ્ન ઉત્થાનની તકલીફ (ED)", "શીઘ્રપતન", "વીર્યસ્ખલનમાં વિલંબ કે ન થવું", "પ્રદર્શનની ચિંતા (પરફોર્મન્સ એન્ઝાયટી)", "ધાત સિન્ડ્રોમ (વીર્ય જવાનો ડર)", "સ્વપ્નદોષ અને હસ્તમૈથુનનો અપરાધભાવ", "લિંગના આકાર–માપની ચિંતા", "જાતીય ઈચ્છામાં ઘટાડો"] },
  { label: "સ્ત્રીઓની જાતીય તકલીફો", items: ["જાતીય ઈચ્છામાં ઘટાડો", "સંભોગ સમયે દુખાવો", "યોનિસંકોચ (વજાઈનિઝમસ)", "ચરમસુખ ન અનુભવાવું", "ઉત્તેજના ન થવી", "સંભોગ પ્રત્યે અણગમો", "પ્રસૂતિ પછી અને મેનોપોઝ પછીના ફેરફાર", "શરીરના દેખાવ અંગેની ચિંતા"] },
  { label: "દામ્પત્ય અને સંબંધ", items: ["લગ્ન પછી સંભોગ ન થઈ શકવો", "પતિ-પત્નીની ઈચ્છામાં તફાવત", "દામ્પત્ય કલહ અને અંતરાય", "સંતાન ન થવાનો તણાવ", "વાતચીત અને વિશ્વાસની સમસ્યા", "લાંબા સમયથી શારીરિક સંબંધ ન હોવો"] },
  { label: "બીમારી અને દવા સંબંધિત", items: ["ડાયાબિટીસ અને બ્લડપ્રેશરમાં જાતીય તકલીફ", "દવાની આડઅસરથી થતી તકલીફ", "બીમારી કે ઓપરેશન પછીની જાતીય સ્વસ્થતા", "હતાશા–ચિંતા સાથે જોડાયેલી તકલીફ", "પોર્ન અને અતિ હસ્તમૈથુનની લત", "લૈંગિક ઓળખ અંગેની મૂંઝવણ", "વ્યસનમુક્તિ પછીની જાતીય સ્વસ્થતા"] },
];

const approach = [
  { icon: BookOpen, title: "સાચી માહિતી અને ગેરમાન્યતા દૂર કરવી", desc: "અમારે આવતી મોટાભાગની તકલીફોનું મૂળ ખોટી માહિતી છે. પહેલાં તબીબી સત્ય સમજાવીએ છીએ — શાંતિથી, કોઈ ટીકા વગર." },
  { icon: MessageSquareHeart, title: "સેક્સ થેરાપી અને CBT", desc: "સેન્સેટ ફોકસ, સ્ટોપ–સ્ટાર્ટ ટેકનિક, ચિંતા ઘટાડવાની કસરત અને વિચાર બદલવાની થેરાપી — નિયત બેઠકોમાં." },
  { icon: Users, title: "દંપતી કાઉન્સેલિંગ", desc: "બંને પાત્ર તૈયાર હોય ત્યારે સારવાર ઝડપી થાય છે. બેઠકો સાથે કે અલગ — તમારી સગવડ પ્રમાણે." },
  { icon: Stethoscope, title: "તબીબી અને દવાની સારવાર", desc: "જ્યાં જરૂરી હોય ત્યાં પુરાવા આધારિત દવા, MD મનોચિકિત્સક દ્વારા — કોઈ 'કોર્સ' કે પેકેજ વેચવામાં આવતું નથી." },
  { icon: FlaskConical, title: "લોહી અને હોર્મોનની તપાસ", desc: "જરૂર પડે ત્યારે સુગર, થાઈરોઈડ, ટેસ્ટોસ્ટેરોન, પ્રોલેક્ટિન અને લિપિડ પ્રોફાઈલ — કારણ કે જાતીય તકલીફ ઘણીવાર શારીરિક રોગનો પહેલો સંકેત હોય છે." },
  { icon: HeartHandshake, title: "જરૂર પડે તો નિષ્ણાત પાસે", desc: "યુરોલોજી, ગાયનેક કે એન્ડોક્રાઈન અભિપ્રાય જરૂરી હોય ત્યારે ભાવનગરના વિશ્વસનીય નિષ્ણાતો સાથે સંકલન." },
];

const promises = [
  { icon: EyeOff, label: "વેઇટિંગમાં નામ બોલાતું નથી", desc: "તમને ખાનગી રીતે અંદર બોલાવવામાં આવે છે. તમારી મુલાકાત વિશે કંઈ જાહેર થતું નથી." },
  { icon: FileLock2, label: "રેકોર્ડ ગુપ્ત રહે છે", desc: "તમારી લેખિત મંજૂરી વગર કેસ પેપર પરિવાર, નોકરીદાતા કે કોઈને પણ બતાવવામાં આવતા નથી." },
  { icon: Lock, label: "એકલા આવો કે સાથે — તમારી પસંદ", desc: "પરામર્શમાં કોણ સાથે રહેશે અને શું ચર્ચા થશે તે તમે નક્કી કરો છો." },
  { icon: ShieldCheck, label: "ફક્ત લાયક ડૉક્ટર", desc: "MD ન્યુરોસાઇકિયાટ્રી નિષ્ણાત દ્વારા સારવાર — કોઈ અધકચરા 'ક્લિનિક' કે ભૂવા-હકીમ નહીં." },
];

const myths = [
  { m: "\"સ્વપ્નદોષથી શરીર નબળું પડે છે.\"", f: "રાત્રે વીર્યસ્ખલન એક સામાન્ય શારીરિક ક્રિયા છે. તેનાથી નબળાઈ, નપુંસકતા કે વંધ્યત્વ આવતું નથી." },
  { m: "\"હસ્તમૈથુનથી કાયમી નુકસાન થાય છે.\"", f: "વૈજ્ઞાનિક રીતે કોઈ શારીરિક નુકસાન સાબિત થયું નથી. તકલીફ ક્રિયાથી નહીં, અપરાધભાવથી થાય છે." },
  { m: "\"ઉત્થાનની તકલીફ એટલે જીવન પૂરું.\"", f: "૯૦%થી વધુ કિસ્સામાં સારવાર શક્ય છે — અને એ ઘણીવાર ડાયાબિટીસ જેવા રોગનો પહેલો સંકેત હોય છે." },
  { m: "\"જાતીય સારવાર ફક્ત પુરુષોને જ જરૂરી.\"", f: "સ્ત્રીઓમાં પણ આ તકલીફો એટલી જ સામાન્ય અને એટલી જ સારવારપાત્ર છે. અવરોધ શરીરનો નથી, મૌનનો છે." },
];

const faqs = [
  { q: "ડૉક્ટર મારી વાત સાંભળીને ટીકા કરશે?", a: "નહીં. અમે મનોચિકિત્સક છીએ — સાંભળવું, ટીકા વગર, અમારી તાલીમનો પાયો છે. તમે જે કહેશો તે લગભગ ચોક્કસ કોઈ બીજા દર્દીએ પણ આ જ ખુરશીમાં બેસીને કહ્યું હશે." },
  { q: "પરિવાર કે પત્નીને જણાવ્યા વગર આવી શકાય?", a: "હા. પરામર્શ માટે કોઈની મંજૂરી કે હાજરી જરૂરી નથી, અને તમારી સંમતિ વગર પરિવારને કંઈ જણાવવામાં આવતું નથી." },
  { q: "નાનપણમાં થયેલી ટેવને કારણે આ તકલીફ છે?", a: "લગભગ ચોક્કસ નહીં. હસ્તમૈથુનથી ઉત્થાનની તકલીફ, વંધ્યત્વ, નબળાઈ કે શીઘ્રપતન થતું નથી — તેવું કોઈ વૈજ્ઞાનિક પુરાવો નથી. પરંતુ તેના વિશેનો ડર અને અપરાધભાવ ખરેખર તકલીફ સર્જી શકે છે, અને તે મટે છે." },
  { q: "હું અપરિણીત છું, તો પણ સલાહ લઈ શકું?", a: "બિલકુલ. અમારા ઘણા દર્દીઓ અપરિણીત છે, અને કેટલાક તો લગ્ન પહેલાં સાચી માહિતી મેળવવા જ આવે છે — સાંભળેલી ખોટી વાતોના બદલે." },
  { q: "શારીરિક તપાસ કરાવવી પડશે?", a: "ફક્ત તબીબી રીતે જરૂરી હોય ત્યારે અને તમારી સંમતિથી. ઘણી તકલીફોનું નિદાન ફક્ત ઇતિહાસ પરથી થાય છે; તપાસ કે રિપોર્ટ જરૂરી હોય તો કારણ સમજાવીને જ કરાય છે." },
  { q: "એકલા હોઉં ત્યારે બરાબર થાય છે પણ પત્ની સાથે નહીં — શારીરિક કે માનસિક?", a: "આ પેટર્ન મોટેભાગે માનસિક કારણ કે પરફોર્મન્સ એન્ઝાયટી સૂચવે છે, બંધારણીય ખામી નહીં — જે સારા સમાચાર છે, કારણ કે તે સેક્સ થેરાપીથી ખૂબ સારી રીતે મટે છે." },
  { q: "લગ્નના મહિનાઓ પછી પણ સંભોગ થઈ શક્યો નથી. મટશે?", a: "હા. આ સ્થિતિ સામાન્ય અને ખૂબ સારવારપાત્ર છે — સામાન્ય રીતે યોનિસંકોચ, પરફોર્મન્સ એન્ઝાયટી કે સાચી માહિતીના અભાવે થાય છે. મોટાભાગના દંપતી થોડી નિયત બેઠકોમાં સુધરે છે." },
  { q: "દવા જીવનભર લેવી પડશે? આદત પડી જશે?", a: "મોટાભાગની જાતીય તકલીફોમાં દવા ટૂંકા ગાળાની અને થેરાપીની સાથે સહાયક હોય છે, અને ધીમે ધીમે બંધ કરાય છે. અમે ક્યારેય અંતહીન 'કોર્સ' કે ટોનિક લખતા નથી." },
  { q: "તમે શક્તિની ગોળી, તેલ કે આયુર્વેદિક દવા વેચો છો?", a: "નહીં. અમે હોસ્પિટલ છીએ, દુકાન નહીં. ફક્ત પુરાવા આધારિત સારવાર આપીએ છીએ — અને જ્યાં કંઈ લખવાની જરૂર ન હોય ત્યાં પ્રામાણિકપણે એમ પણ કહીએ છીએ." },
  { q: "મોઢેથી કહેવામાં શરમ આવે તો?", a: "તમે લખીને આપી શકો છો — અથવા નીચે આપેલું ગુપ્ત પ્રશ્ન ફોર્મ વાપરી શકો છો. ઘણા દર્દીઓ એ રીતે શરૂ કરીને પછી રૂબરૂ આવે છે." },
  { q: "ફોન કે વિડિયો પર પરામર્શ થાય?", a: "હા, ફોલો-અપ અને શરૂઆતની ચર્ચા માટે. કેટલીક તકલીફોમાં રૂબરૂ તપાસ જરૂરી હોય છે, અને તે તમને સ્પષ્ટ જણાવવામાં આવશે." },
  { q: "ખર્ચ કેટલો થશે?", a: "સામાન્ય OPD કન્સલ્ટેશન ફી — બીજા કોઈ પણ માનસિક પરામર્શ જેટલી જ. કોઈ પેકેજ નહીં, છૂપો ખર્ચ નહીં, અને 'પ્લાન' લેવાનું દબાણ નહીં. વર્તમાન ફી માટે ક્લિનિકમાં ફોન કરો." },
];

const JSONLD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalClinic",
      "@id": `${SITE}/#sexology-gu`,
      name: "માનસ સેક્સ થેરાપી ક્લિનિક, ભાવનગર",
      alternateName: "Manas Sex Therapy Clinic, Bhavnagar",
      medicalSpecialty: "Psychiatry",
      inLanguage: "gu-IN",
      url: `${SITE}/gu/sexology`,
      telephone: "+91-94275-60101",
      foundingDate: "1989",
      address: {
        "@type": "PostalAddress",
        streetAddress: "2nd floor, Carlton Square, Near Madhavdeep Complex, Kalanala",
        addressLocality: "Bhavnagar",
        postalCode: "364001",
        addressRegion: "Gujarat",
        addressCountry: "IN",
      },
      areaServed: ["Bhavnagar", "Sihor", "Palitana", "Talaja", "Mahuva", "Botad", "Amreli", "Saurashtra", "Gujarat"],
      openingHoursSpecification: [{
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
        opens: "09:00",
        closes: "20:00",
      }],
    },
    {
      "@type": "FAQPage",
      inLanguage: "gu-IN",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ],
};

export default function SexologyGu() {
  useSEO({
    title: "ભાવનગરમાં સેક્સોલોજિસ્ટ · જાતીય તકલીફોની ગુપ્ત સારવાર",
    description:
      "ભાવનગરની માનસ હોસ્પિટલનું સેક્સ થેરાપી ક્લિનિક — શીઘ્રપતન, ઉત્થાનની તકલીફ, ધાત સિન્ડ્રોમ, જાતીય ઈચ્છામાં ઘટાડો, યોનિસંકોચ, લગ્ન પછી સંભોગ ન થવો અને દામ્પત્ય સમસ્યાઓની સંપૂર્ણ ગુપ્ત, પુરાવા આધારિત સારવાર. ડૉ. કમલેશ શાહ અને ડૉ. ધર્મિન શાહ. ફોન +૯૧ ૯૪૨૭૫ ૬૦૧૦૧.",
    image: HERO,
    jsonLd: JSONLD,
    lang: "gu",
    alternates: [
      { hreflang: "en-IN", href: `${SITE}/sexology` },
      { hreflang: "gu-IN", href: `${SITE}/gu/sexology` },
      { hreflang: "x-default", href: `${SITE}/sexology` },
    ],
  });
  const [open, setOpen] = useState(false);

  return (
    <div data-testid="sexology-gu-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-16">
        <div className="flex items-center gap-3 mb-6 flex-wrap">
          <p className="eyebrow text-[#A39171]">સેક્સોલોજી · લૈંગિક સ્વાસ્થ્ય</p>
          <Link
            to="/sexology"
            data-testid="sx-gu-switch-english"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#A39171]/40 text-[#5C524E] hover:border-[#E07A5F] hover:text-[#E07A5F] transition-colors"
          >
            <Languages className="w-3.5 h-3.5" /> Read in English
          </Link>
        </div>
        <h1 className="font-display text-4xl md:text-6xl text-[#2D2623] leading-[1.1] tracking-tight max-w-4xl">
          સૌથી અઘરું કામ છે <br />
          <span className="italic text-[#E07A5F]">મોઢેથી કહેવું</span>.
        </h1>
        <p className="mt-8 text-lg md:text-xl text-[#5C524E] max-w-3xl leading-relaxed">
          માનસિક હોસ્પિટલની અંદર ચાલતું અલાયદું સેક્સ થેરાપી ક્લિનિક — જ્યાં જાતીય સ્વાસ્થ્યને શરમ
          નહીં, દવાની જેમ સારવાર મળે છે. પુરુષો, સ્ત્રીઓ અને દંપતી માટે ખાનગી પરામર્શ, સાચા જવાબ અને
          પુરાવા આધારિત સારવાર.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <Button data-testid="sx-gu-hero-book" onClick={() => setOpen(true)} className="rounded-full bg-[#2D2623] hover:bg-[#E07A5F] text-[#FDFBF7] px-8 h-14 font-medium">
            ખાનગી પરામર્શ માટે સમય લો <ArrowUpRight className="ml-1 w-4 h-4" />
          </Button>
          <a href="tel:+919427560101" data-testid="sx-gu-hero-call" className="rounded-full border border-[#2D2623]/20 hover:border-[#E07A5F] hover:text-[#E07A5F] text-[#2D2623] px-8 h-14 inline-flex items-center justify-center font-medium transition-colors">
            ગુપ્ત રીતે વાત કરો
          </a>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 mb-24">
        <div className="rounded-3xl overflow-hidden aspect-[16/9] md:aspect-[21/9] relative">
          <img src={HERO} alt="માનસ હોસ્પિટલના સેક્સ થેરાપી ક્લિનિકમાં દંપતીનું પરામર્શ" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1513]/90 via-[#1A1513]/45 to-transparent" />
          <div className="absolute bottom-8 left-8 right-8 md:left-12 md:right-auto md:max-w-xl">
            <p className="eyebrow text-[#F0CF5B] mb-3 drop-shadow-[0_2px_8px_rgba(0,0,0,0.65)]">૧૯૮૯થી · ભાવનગર</p>
            <p className="font-display text-2xl md:text-3xl leading-snug text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.7)]">
              બે પેઢીના મનોચિકિત્સકો. એક શાંત કેબિન. કોઈ ટીકા નહીં.
            </p>
          </div>
        </div>
      </section>

      {/* CONFIDENTIALITY */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="bg-[#2D2623] text-[#FDFBF7] rounded-3xl p-8 md:p-14 relative overflow-hidden">
            <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
            <div className="relative grid grid-cols-1 md:grid-cols-12 gap-10">
              <div className="md:col-span-5">
                <span className="w-12 h-12 rounded-2xl bg-[#E07A5F] text-white flex items-center justify-center mb-6">
                  <Lock className="w-5 h-5" />
                </span>
                <p className="eyebrow text-[#E2C044] mb-4">૧૦૦% ગુપ્તતા</p>
                <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-snug">
                  જે અહીં કહેવાય છે, <span className="italic text-[#E07A5F]">તે અહીં જ રહે છે</span>.
                </h2>
                <p className="text-[#FDFBF7]/75 mt-6 leading-relaxed">
                  દરેક સેક્સોલોજી પરામર્શ અલગ, બંધ કેબિનમાં અને અલાયદા સમયે થાય છે — જેથી ક્લિનિકમાં
                  બીજા કોઈને સ્પષ્ટતા આપવાની જરૂર ન પડે.
                </p>
              </div>
              <div className="md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {promises.map((p, i) => (
                  <div key={p.label} data-testid={`sx-gu-privacy-${i}`}
                    className="bg-[#FDFBF7]/5 border border-[#FDFBF7]/15 rounded-2xl p-6 hover:bg-[#FDFBF7]/10 transition-colors">
                    <p.icon className="w-5 h-5 text-[#E2C044] mb-4" />
                    <h3 className="font-display text-lg md:text-xl leading-snug">{p.label}</h3>
                    <p className="text-sm text-[#FDFBF7]/70 mt-2 leading-relaxed">{p.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONDITIONS */}
      <section className="bg-[#F4EFE6]/60 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl mb-14">
            <p className="eyebrow text-[#A39171] mb-4">કઈ જાતીય તકલીફોની સારવાર</p>
            <h2 className="font-display text-3xl md:text-5xl text-[#2D2623] tracking-tight leading-snug">
              તમે જે લાવો છો તે <span className="italic text-[#E07A5F]">અમારા માટે નવું નથી</span>.
            </h2>
            <p className="mt-6 text-lg text-[#5C524E] leading-relaxed">
              લગભગ દર ત્રણમાંથી એક વ્યક્તિને જીવનમાં ક્યારેક જાતીય તકલીફ થાય છે. અમારું ક્લિનિક નીચેની
              સંપૂર્ણ શ્રેણીની સારવાર કરે છે — વ્યક્તિગત રીતે અને દંપતી માટે.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {groups.map((g, gi) => (
              <div key={g.label} data-testid={`sx-gu-group-${gi}`}
                className="bg-[#FDFBF7] rounded-3xl p-8 md:p-9 border border-[#A39171]/15 hover:shadow-[0_20px_50px_rgba(163,145,113,0.12)] transition-shadow">
                <div className="flex items-center gap-3 mb-6">
                  <span className="font-display text-3xl text-[#E07A5F]">0{gi + 1}</span>
                  <h3 className="font-display text-xl md:text-2xl text-[#2D2623] tracking-tight leading-snug">{g.label}</h3>
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  {g.items.map((it) => (
                    <li key={it} className="flex items-start gap-2.5 text-[15px] text-[#5C524E]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#E2C044] mt-2 shrink-0" />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* APPROACH */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl mb-14">
            <p className="eyebrow text-[#A39171] mb-4">અમારી સારવાર પદ્ધતિ</p>
            <h2 className="font-display text-3xl md:text-5xl text-[#2D2623] tracking-tight leading-snug">
              પહેલાં સાચી માહિતી. <span className="italic text-[#E07A5F]">પછી થેરાપી. પછી દવા</span>.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {approach.map((a, i) => (
              <div key={a.title} data-testid={`sx-gu-approach-${i}`}
                className="bg-[#F4EFE6] rounded-3xl p-8 border border-[#A39171]/15 hover:border-[#E07A5F]/40 transition-all">
                <span className="w-12 h-12 rounded-2xl bg-[#2D2623] text-[#FDFBF7] flex items-center justify-center mb-5">
                  <a.icon className="w-5 h-5" />
                </span>
                <h3 className="font-display text-xl md:text-2xl text-[#2D2623] tracking-tight leading-snug">{a.title}</h3>
                <p className="text-[#5C524E] mt-3 text-sm leading-relaxed">{a.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MYTHS */}
      <section className="bg-[#F4EFE6]/60 py-24 md:py-32">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">ગેરમાન્યતા અને હકીકત</p>
          <h2 className="font-display text-3xl md:text-5xl text-[#2D2623] tracking-tight mb-14 max-w-3xl leading-snug">
            અડધો ઈલાજ છે <span className="italic text-[#E07A5F]">સાચી માહિતી</span>.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {myths.map((x, i) => (
              <div key={i} data-testid={`sx-gu-myth-${i}`}
                className="bg-[#FDFBF7] rounded-3xl p-8 border border-[#A39171]/15">
                <p className="font-display text-lg md:text-2xl text-[#A39171] italic leading-snug">{x.m}</p>
                <div className="w-10 h-[2px] bg-[#E07A5F] my-5" />
                <p className="text-[#2D2623] leading-relaxed">{x.f}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <p className="eyebrow text-[#A39171] mb-4">પૂછવામાં સંકોચ થાય તેવા પ્રશ્નો</p>
          <h2 className="font-display text-3xl md:text-5xl text-[#2D2623] tracking-tight leading-snug mb-6">
            દર્દીઓ જે <span className="italic text-[#E07A5F]">દરવાજો બંધ થયા પછી જ પૂછે છે</span>.
          </h2>
          <p className="text-lg text-[#5C524E] leading-relaxed mb-14 max-w-2xl">
            તેના જવાબ અહીં સ્પષ્ટ આપ્યા છે — જેથી તમે ઈચ્છો નહીં ત્યાં સુધી મોઢેથી પૂછવું ન પડે.
          </p>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem key={f.q} value={`sxgu-${i}`} data-testid={`sx-gu-faq-${i}`}
                className="bg-[#F4EFE6] rounded-2xl border border-[#A39171]/15 px-6 data-[state=open]:border-[#E07A5F]/35 transition-colors">
                <AccordionTrigger className="text-left font-display text-lg md:text-2xl text-[#2D2623] tracking-tight hover:no-underline py-5 leading-snug">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[#5C524E] text-base md:text-lg leading-relaxed pb-6">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ANONYMOUS ENQUIRY */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <AnonymousEnquiry />
          <p className="mt-6 text-sm text-[#A39171] text-center">
            (ઉપરનું ફોર્મ અંગ્રેજીમાં છે — તમે ગુજરાતીમાં પણ પ્રશ્ન લખી શકો છો.)
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#2D2623] text-[#FDFBF7] py-20 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-30 pointer-events-none" />
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center relative">
          <h2 className="font-display text-3xl md:text-5xl tracking-tight leading-snug">
            તમે પૂરતી વાટ જોઈ લીધી. <span className="italic text-[#E07A5F]">શરૂઆત માટે એક મુલાકાત પૂરતી છે</span>.
          </h2>
          <p className="text-[#FDFBF7]/70 mt-5 max-w-xl mx-auto">
            લાયક મનોચિકિત્સક અને સેક્સોલોજિસ્ટ સાથે ખાનગી પરામર્શ. સંપૂર્ણ ગુપ્તતાની ખાતરી.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Button data-testid="sx-gu-cta-book" onClick={() => setOpen(true)} className="rounded-full bg-[#E07A5F] hover:bg-[#FDFBF7] hover:text-[#2D2623] text-white px-8 h-12 font-medium">સમય માંગો</Button>
            <a href="tel:+919427560101" className="rounded-full border border-[#FDFBF7]/30 hover:bg-[#FDFBF7] hover:text-[#2D2623] px-8 h-12 inline-flex items-center justify-center font-medium transition-colors">ફોન +૯૧ ૯૪૨૭૫ ૬૦૧૦૧</a>
          </div>
        </div>
      </section>

      <AppointmentDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
